import { useState, useEffect, useMemo, useRef } from 'react';
import { createCatalogueSourceColumn, createSourceEntityColumnsBulk, getBronzeCatalogColumns, updateCatalogueData, deleteCatalogueSourceColumn, getDataMartMappings, getDataMartColumns, getSilverCatalogColumns, type CreateSourceEntityColumnInput } from '../../services/catalogue.service';
import { type SourceEntityColumnMapping } from '../../services/dataVaultModelling.service';
import { IDataVaultRelatedEntity } from '../../interfaces/dataVaultModelling.interfaces';
import useDebounce from './useDebounceHook';
import { useEnvironment } from '../../Contexts/EnvironmentContext';
import { useAuth } from '../../Contexts/AuthContext';
import { getDataMarts } from '../../services/dataLinage.service';
import { parseCsvRows } from '../../helpers/csv';
import {
    BRONZE_TEMPLATE_HEADERS,
    buildBronzeTemplateSignatureRow,
    downloadBronzeGuide,
    readBronzeTemplateSignature,
} from './bronzeTemplateGuide';
import { getScopeCustomerId } from '../../services/activeScope';

const DATA_SOURCE_CATEGORY_HIDDEN_FIELDS = new Set([
    "source_entity_name",
    "data_source_name"
]);

const BRONZE_DEFAULT_FIELDS = [
    "source_entity_column_name",
    "logical_name",
    "data_type"
];

export { BRONZE_TEMPLATE_HEADERS };

const CATALOG_ENRICHMENT_FIELDS = [
    "logical_name",
    "description",
    "constraint",
    "data_format",
    "example_values",
    "enable_genai",
    "pii",
    "classification",
    "owner",
    "notes"
] as const;

const DATA_VAULT_CATEGORY_HIDDEN_FIELDS = new Set([
    "source_entity_name",
    "data_source_name",
    "target_entity_rdv_name",
    "name",
    "target_schema"
]);

const SILVER_FIELD_CONFIG = [
    { header: "Column Name", field: "source_entity_column_name", locked: true },
    { header: "Business Key", field: "business_key", locked: true },
    { header: "Hash Key", field: "hash_key", locked: true },
    { header: "Pii", field: "pii", locked: true },
    { header: "Column Transform", field: "target_entity_rdv_column_transform", locked: false },
    { header: "Description", field: "description", locked: false }
];

const SILVER_FIELD_KEYS = SILVER_FIELD_CONFIG.map(item => item.field);
const SILVER_DEFAULT_FIELDS = [
    "source_entity_column_name",
    "business_key",
    "hash_key",
    "description"
];
const SILVER_LOCKED_FIELD_SET = new Set(
    SILVER_FIELD_CONFIG.filter(item => item.locked).map(item => item.field)
);

const GOLD_FIELD_CONFIG = [
    { header: "DM Column Name", field: "target_entity_dm_column_name", locked: true },
    { header: "Description", field: "description", locked: false },
    { header: "Column Transform", field: "column_transform", locked: false },
    { header: "Report View Column Transform", field: "rw_column_transform", locked: false },
    { header: "Dimension Key", field: "dimension_key", locked: true },
    { header: "Dimension Attribute", field: "dimension_attribute", locked: false },
    { header: "Pii", field: "pii", locked: true },
    { header: "Is Active", field: "is_active", locked: false }
];

const GOLD_DEFAULT_FIELDS = ["target_entity_dm_column_name", "dimension_key", "description"];
const GOLD_LOCKED_FIELD_SET = new Set(GOLD_DEFAULT_FIELDS);
const DATA_MART_COLUMNS_DEFAULT_PAGE_SIZE = 10;

const normalizeBronzeCatalogRows = (rows: any[] = [], entity?: any) =>
    rows.map((row) => ({
        data_source_id: entity?.data_source_id ?? null,
        data_source_name: entity?.data_source_name ?? null,
        data_source_type: entity?.data_source_type ?? null,
        source_entity_id: entity?.source_entity_id ?? null,
        source_entity_name: entity?.source_entity_name ?? null,
        source_schema: entity?.source_schema ?? null,
        source_entity_layer: entity?.source_entity_layer ?? null,
        source_entity_kind: entity?.source_entity_kind ?? null,
        source_entity_origin: entity?.source_entity_origin ?? null,
        source_entity_description: entity?.source_entity_description ?? null,
        source_entity_is_active: entity?.is_active ?? true,
        source_entity_column_id: row?.source_entity_column_id ?? null,
        source_entity_column_detail_id: row?.details?.source_entity_column_detail_id ?? null,
        source_entity_column_name: row?.source_entity_column_name ?? null,
        data_type: row?.data_type ?? null,
        is_obsolete: row?.is_obsolete ?? false,
        logical_name: row?.details?.logical_name ?? null,
        description: row?.details?.description ?? null,
        constraint: row?.details?.constraint ?? null,
        data_format: row?.details?.data_format ?? null,
        example_values: row?.details?.example_values ?? null,
        enable_genai: row?.details?.enable_genai ?? false,
        pii: row?.details?.pii ?? false,
        classification: row?.details?.classification ?? null,
        owner: row?.details?.owner ?? null,
        notes: row?.details?.notes ?? null,
    }));

export const useCatalogueData = () => {
    const { activeEnvironmentId } = useEnvironment();
    const { tokens } = useAuth();
    const [data, setData] = useState({ items: [], total_count: 0, page: 1, page_size: 250, data: [], total: 0, total_pages: 0 });
    const [columnsToShow, setColumnsToShow] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [deletingCatalogColumn, setDeletingCatalogColumn] = useState(false);
    const [savingCatalogColumn, setSavingCatalogColumn] = useState(false);
    const [catalogModalMode, setCatalogModalMode] = useState<'create' | 'edit'>('edit');
    const [formattedString, setFormattedString] = useState("");
    const [selectedItems, setSelectedItems] = useState<string[]>([
        ...BRONZE_DEFAULT_FIELDS
    ]);
    const [selectedItemsForFilters, setSelectedItemsForFilters] = useState([]);
    const [modalFormData, setModalFormData] = useState<any>({
        source_entity_column_detail_id: '',
        source_entity_column_id: '',
        logical_name: '',
        description: '',
        constraint: '',
        data_format: '',
        example_values: '',
        enable_genai: undefined,
        pii: undefined,
        classification: "",
        owner: "",
        notes: '',
        created_at: '',
        updated_at: ''
    });
    const [modalOriginalData, setModalOriginalData] = useState<any | null>(null);
    const [formData, setFormData] = useState<any>({
        source_entity_column_detail_id: '',
        source_entity_column_id: '',
        logical_name: '',
        description: '',
        constraint: '',
        data_format: '',
        example_values: '',
        enable_genai: undefined,
        pii: undefined,
        classification: "",
        owner: "",
        notes: '',
        created_at: '',
        updated_at: ''
    });
    const [loading, setLoading] = useState(false);
    const [formFields, setFormFields] = useState([]);
    const [multipleSelection, setMultipleSelection] = useState<string[]>([]);
    const [selectedEndpoints, setSelectedEndpoints] = useState<string[]>([]);
    const [bronzeSelectionIdentity, setBronzeSelectionIdentity] = useState<{
        dataSourceId: number | null;
        sourceEntityId: number | null;
        sourceEntityLayer: "bronze" | "silver" | "gold" | null;
        dataSourceType: string | null;
    }>({
        dataSourceId: null,
        sourceEntityId: null,
        sourceEntityLayer: null,
        dataSourceType: null,
    });

    const [showErrorModal, setShowErrorModal] = useState(false);
    const [dataMartMappings, setDataMartMappings] = useState<any[]>([]);
    const [dataMartOptions, setDataMartOptions] = useState<any[]>([]);
    const [selectedDataMartEntities, setSelectedDataMartEntities] = useState<string[]>([]);
    const [selectedDataMarts, setSelectedDataMarts] = useState<string[]>([]);
    const [isLoadingDataMartMappings, setIsLoadingDataMartMappings] = useState(false);
    const [activeRelatedEntity, setActiveRelatedEntity] = useState<IDataVaultRelatedEntity | null>(null);
    const [relatedEntityData, setRelatedEntityData] = useState<any[]>([]);
    const [relatedEntityColumns, setRelatedEntityColumns] = useState<any[]>([]);
    const [relatedEntityLoading, setRelatedEntityLoading] = useState(false);
    const [relatedEntityPage, setRelatedEntityPage] = useState(1);
    const [relatedEntityTotal, setRelatedEntityTotal] = useState(0);
    const [relatedEntityPageSize, setRelatedEntityPageSize] = useState(25);
    const [relatedEntityHasMore, setRelatedEntityHasMore] = useState(false);
    const [relatedEntityTotalKnown, setRelatedEntityTotalKnown] = useState(true);
    const [dataMartEntityColumns, setDataMartEntityColumns] = useState<any[]>([]);
    const [dataMartEntityData, setDataMartEntityData] = useState<any[]>([]);
    const [dataMartEntityLoading, setDataMartEntityLoading] = useState(false);
    const [dataMartEntityPage, setDataMartEntityPage] = useState(1);
    const [dataMartEntityPageSize, setDataMartEntityPageSize] = useState(DATA_MART_COLUMNS_DEFAULT_PAGE_SIZE);
    const [dataMartEntityTotal, setDataMartEntityTotal] = useState(0);
    const [silverSelectedFields, setSilverSelectedFields] = useState<string[]>([
        ...SILVER_DEFAULT_FIELDS
    ]);
    const [goldSelectedFields, setGoldSelectedFields] = useState<string[]>([
        ...GOLD_DEFAULT_FIELDS
    ]);

    const [currentPage, setCurrentPage] = useState(1);

    const [apiFailedStatus, setApiFailedStatus] = useState(false)

    const [isLoadingDownloadCSV, setIsLoadingDownloadCSV] = useState(false);
    const [isImportingBronzeCsv, setIsImportingBronzeCsv] = useState(false);
    const [pendingBronzeImportRows, setPendingBronzeImportRows] = useState<any[]>([]);

    const hasMounted = useRef(false);
    const previousDataMartEntityRef = useRef<string | null>(null);
    const bronzeInFlightRequestKeyRef = useRef<string | null>(null);

    const itemsPerPage = 25;
    const bronzeFetchLimit = 250;
    const explorerFetchLimit = 250;
    const isInternalSourceEntitySelected = bronzeSelectionIdentity.dataSourceType === "INTERNAL_REGISTERED";

    useEffect(() => {
        setPendingBronzeImportRows([]);
    }, [bronzeSelectionIdentity.dataSourceId, bronzeSelectionIdentity.sourceEntityId]);

    const createEmptyModalFormData = () => ({
        source_entity_column_detail_id: '',
        source_entity_column_id: '',
        logical_name: '',
        description: '',
        constraint: '',
        data_format: '',
        example_values: '',
        enable_genai: false,
        pii: false,
        classification: "",
        owner: "",
        notes: '',
        created_at: '',
        updated_at: '',
        source_entity_column_name: '',
        data_type: 'string',
        source_entity_name: selectedEndpoints?.[0] ?? '',
        data_source_name: multipleSelection?.[0] ?? '',
        data_source_type: bronzeSelectionIdentity.dataSourceType,
        source_entity_id: bronzeSelectionIdentity.sourceEntityId,
        data_source_id: bronzeSelectionIdentity.dataSourceId,
        source_entity_layer: bronzeSelectionIdentity.sourceEntityLayer,
        is_obsolete: false,
    });

    const refreshSourceEntityColumns = async (sourceEntityId: number) => {
        const res: any = await getBronzeCatalogColumns({
            sourceEntityId,
            page: 1,
            limit: bronzeFetchLimit,
        });
        const payload = Array.isArray(res?.items) ? res.items : [];
        const augmentedItems = normalizeBronzeCatalogRows(payload, res?.entity);
        setData({
            ...res,
            items: augmentedItems,
            total_count: augmentedItems.length,
            data: augmentedItems,
            total: augmentedItems.length,
        });
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const handleRelatedEntityPageChange = (pageNumber: number) => {
        setRelatedEntityPage(pageNumber);
    };

    const handleDataMartEntityPageChange = (pageNumber: number) => {
        setDataMartEntityPage(pageNumber);
    };

    useEffect(() => {
        if (selectedItemsForFilters[0] !== "") {
            const updatedFormFields = selectedItemsForFilters.map(field => ({
                controlId: `form${field.charAt(0).toUpperCase() + field.slice(1)}`,
                label: field.split('_').map((word: any) => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
                type: 'text',
                name: field
            }));

            setFormFields(updatedFormFields);
        }
    }, [selectedItemsForFilters])

    useEffect(() => {
        let isMounted = true;
        setIsLoadingDataMartMappings(true);
        getDataMartMappings()
            .then(data => {
                if (isMounted) {
                    setDataMartMappings(data ?? []);
                }
            })
            .catch(error => console.error('Error fetching data mart mappings:', error))
            .finally(() => {
                if (isMounted) {
                    setIsLoadingDataMartMappings(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        getDataMarts()
            .then((response) => {
                if (!isMounted) {
                    return;
                }
                const records = Array.isArray(response) ? response : [];
                setDataMartOptions(records);
            })
            .catch((error) => {
                console.error('Error fetching data mart list:', error);
                if (isMounted) {
                    setDataMartOptions([]);
                }
            });
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        setRelatedEntityPage(1);
    }, [
        activeRelatedEntity?.target_entity_rdv_id,
        activeRelatedEntity?.source_entity_id
    ]);

    useEffect(() => {
        let isActive = true;
        if (
            !activeRelatedEntity ||
            !activeRelatedEntity.target_entity_rdv_id
        ) {
            setRelatedEntityData([]);
            setRelatedEntityColumns([]);
            setRelatedEntityLoading(false);
            setRelatedEntityTotal(0);
            setRelatedEntityPageSize(itemsPerPage);
            setRelatedEntityHasMore(false);
            setRelatedEntityTotalKnown(true);
            return () => {
                isActive = false;
            };
        }

        setRelatedEntityLoading(true);
        getSilverCatalogColumns(Number(activeRelatedEntity.target_entity_rdv_id), explorerFetchLimit)
            .then(response => {
                if (!isActive) return;
                const payload: SourceEntityColumnMapping[] = Array.isArray((response as any)?.items)
                    ? (response as any).items
                    : Array.isArray(response)
                        ? response
                        : [];
                const responseMeta = (response as any) || {};
                const sortedPayload = [...payload].sort((a, b) => {
                    const aNull = !a?.business_key;
                    const bNull = !b?.business_key;
                    if (aNull && !bNull) return -1;
                    if (!aNull && bNull) return 1;
                    return 0;
                });

                const resolvedTotal = typeof responseMeta?.total_count === "number"
                    ? responseMeta.total_count
                    : sortedPayload.length;
                const resolvedPageSize = typeof responseMeta?.page_size === "number"
                    ? responseMeta.page_size
                    : explorerFetchLimit;
                const resolvedTotalPages = resolvedPageSize > 0
                    ? Math.max(1, Math.ceil(resolvedTotal / resolvedPageSize))
                    : 1;

                const augmentedPayload = sortedPayload;
                setRelatedEntityTotal(augmentedPayload.length || resolvedTotal);
                setRelatedEntityPageSize(itemsPerPage);
                setRelatedEntityHasMore(relatedEntityPage < resolvedTotalPages);
                setRelatedEntityTotalKnown(true);
                setRelatedEntityData(augmentedPayload);
                if (augmentedPayload.length > 0) {
                    setRelatedEntityColumns(
                        convertObjectToColumns(augmentedPayload[0], {
                            excludeFields: DATA_VAULT_CATEGORY_HIDDEN_FIELDS,
                        })
                    );
                } else {
                    setRelatedEntityColumns([]);
                }
            })
            .catch(error => {
                console.error("Error fetching related entity columns:", error);
                if (isActive) {
                    setRelatedEntityData([]);
                    setRelatedEntityColumns([]);
                    setRelatedEntityTotal(0);
                    setRelatedEntityHasMore(false);
                }
            })
            .finally(() => {
                if (isActive) {
                    setRelatedEntityLoading(false);
                }
            });

        return () => {
            isActive = false;
        };
    }, [activeRelatedEntity?.target_entity_rdv_id]);

    const fetchResults = async () => {
        const requestKey = JSON.stringify({
            sourceEntityId: bronzeSelectionIdentity.sourceEntityId,
        });

        if (bronzeInFlightRequestKeyRef.current === requestKey) {
            return;
        }

        try {
            bronzeInFlightRequestKeyRef.current = requestKey;
            setLoading(true)
            await getBronzeCatalogColumns({
                sourceEntityId: Number(bronzeSelectionIdentity.sourceEntityId),
                page: 1,
                limit: bronzeFetchLimit,
            })
                .then((res: any) => {
                    const payload = Array.isArray(res?.items) ? res.items : [];
                    const augmentedItems = normalizeBronzeCatalogRows(payload, res?.entity);
                    setData({
                        ...res,
                        items: augmentedItems,
                        total_count: augmentedItems.length,
                        data: augmentedItems,
                        total: augmentedItems.length,
                    });
                });
        } catch (error) {
            console.error('Error fetching results:', error);
            setApiFailedStatus(true)
        } finally {
            if (bronzeInFlightRequestKeyRef.current === requestKey) {
                bronzeInFlightRequestKeyRef.current = null;
            }
            setLoading(false)
        }
    };

    const getCatalogueFilteredDataExportURL = (
        columns: any,
        filters: any,
        source: any = [],
        endpoint: any = [],
        page: any,
        limit = bronzeFetchLimit,
        dataMartEntities: any[] = [],
    ) => {
        if (!activeEnvironmentId) {
            return "";
        }

        if (
            !isRelatedEntityMode &&
            !isDataMartMode &&
            Number.isFinite(Number(bronzeSelectionIdentity.sourceEntityId))
        ) {
            const params = new URLSearchParams({
                environment_id: String(activeEnvironmentId),
                customer_id: String(getScopeCustomerId() ?? ""),
                source_entity_id: String(bronzeSelectionIdentity.sourceEntityId),
            });

            return `${process.env.REACT_APP_API_URL}catalog/bronze-columns/export?${params.toString()}`;
        }

        const baseUrl = `${process.env.REACT_APP_API_URL}environment/${activeEnvironmentId}/data_catalogue_filtered`;

        const params = new URLSearchParams();

        params.set("export", "true");;

        params.set("page", String(page));
        params.set("limit", String(limit));
        params.set("columns", columns);

        if (filters) {
            params.set("filters", encodeURIComponent(filters));
        }

        if (source.length) {
            params.set("source", source.map(encodeURIComponent).join(","));
        }
        if (endpoint.length) {
            params.set("endpoint", endpoint.map(encodeURIComponent).join(","));
        }
        if (dataMartEntities.length) {
            params.set("data_mart", dataMartEntities.map(encodeURIComponent).join(","));
        }

        const url = `${baseUrl}?${params.toString()}`;

        return url
    };
    const sanitizeExportSegment = (value?: string | null) => {
        if (!value) {
            return "";
        }

        return value
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/_{2,}/g, "_")
            .replace(/^_|_$/g, "");
    };

    const buildExportFilename = () => {
        if (isDataMartMode) {
            const entityName = sanitizeExportSegment(selectedDataMartEntities?.[0]);
            return entityName ? `gold_${entityName}.csv` : "gold_catalog.csv";
        }

        if (isRelatedEntityMode) {
            const entityName = sanitizeExportSegment(
                activeRelatedEntity?.target_entity_rdv_name || activeRelatedEntity?.source_entity_name
            );
            return entityName ? `silver_${entityName}.csv` : "silver_catalog.csv";
        }

        const sourceName = sanitizeExportSegment(multipleSelection?.[0]);
        const endpointName = sanitizeExportSegment(selectedEndpoints?.[0]);
        const sourceLayer = sanitizeExportSegment(bronzeSelectionIdentity.sourceEntityLayer || "bronze");

        if (sourceName && endpointName) {
            return `${sourceLayer}_${sourceName}_${endpointName}.csv`;
        }
        if (endpointName) {
            return `${sourceLayer}_${endpointName}.csv`;
        }
        if (sourceName) {
            return `${sourceLayer}_${sourceName}.csv`;
        }

        return "catalog_export.csv";
    };

    function downloadCSV() {
        const url = getCatalogueFilteredDataExportURL(
            visibleSelectedItems,
            formattedString,
            multipleSelection,
            selectedEndpoints,
            currentPage,
            undefined,
            selectedDataMartEntities
        )
        if (!url) {
            console.warn("Cannot download catalogue CSV without an active environment");
            return;
        }
        if (!tokens.accessToken) {
            console.warn("Cannot download CSV without an access token");
            return;
        }
        setIsLoadingDownloadCSV(true)
        fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${tokens.accessToken}`
            }
        })
            .then(response => {
                if (!response.ok) throw new Error('Network response was not ok');
                return response.blob();
            })
            .then(blob => {
                const downloadUrl = window.URL.createObjectURL(blob);
                const csvElement = document.createElement('a');
                csvElement.href = downloadUrl;
                csvElement.download = buildExportFilename();
                document.body.appendChild(csvElement);
                csvElement.click();
                csvElement.remove();
                window.URL.revokeObjectURL(downloadUrl);
            })
            .catch(error => {
                console.error('Error downloading CSV:', error);
            })
            .finally(() => {
                setIsLoadingDownloadCSV(false);
            });
    }

    const buildBronzeTemplateFilename = () => {
        const sourceName = sanitizeExportSegment(multipleSelection?.[0]);
        const entityName = sanitizeExportSegment(selectedEndpoints?.[0]);
        const name = [sourceName, entityName].filter(Boolean).join("_");
        return name ? `bronze_${name}_metadata_template.csv` : "bronze_metadata_template.csv";
    };

    const downloadBronzeTemplate = () => {
        if (!hasBronzeObjectSelection) {
            return;
        }

        const sourceEntityId = Number(bronzeSelectionIdentity.sourceEntityId);
        const sourceEntityName = selectedEndpoints?.[0]?.trim() ?? "";
        if (!Number.isSafeInteger(sourceEntityId) || sourceEntityId <= 0 || !sourceEntityName) {
            return;
        }

        const csv = BRONZE_TEMPLATE_HEADERS.join(",");
        const signature = buildBronzeTemplateSignatureRow(sourceEntityId, sourceEntityName);
        const blob = new Blob([`\ufeff${signature}\r\n${csv}\r\n`], { type: "text/csv;charset=utf-8;" });
        const downloadUrl = window.URL.createObjectURL(blob);
        const csvElement = document.createElement("a");
        csvElement.href = downloadUrl;
        csvElement.download = buildBronzeTemplateFilename();
        document.body.appendChild(csvElement);
        csvElement.click();
        csvElement.remove();
        window.URL.revokeObjectURL(downloadUrl);
    };

    const downloadBronzeGuideFile = async () => {
        if (!hasBronzeObjectSelection) {
            return;
        }

        const entitySample = Array.isArray((data as any)?.items) ? (data as any).items[0] : null;
        await downloadBronzeGuide(
            {
                entityName: entitySample?.source_entity_name ?? selectedEndpoints?.[0],
                entityDescription: entitySample?.source_entity_description,
                dataSourceName: entitySample?.data_source_name ?? multipleSelection?.[0],
                schemaName: entitySample?.source_schema,
            },
            buildBronzeTemplateFilename().replace(/_metadata_template\.csv$/, "_metadata_guide.xlsx"),
        );
    };

    const importBronzeCsv = async (csvText: string) => {
        if (!hasBronzeObjectSelection) {
            throw new Error("Select a bronze source entity before importing a CSV.");
        }

        const parsedRows = parseCsvRows(csvText);
        if (parsedRows.length === 0) {
            throw new Error("The CSV file must contain a header row and at least one record.");
        }

        const templateSignature = readBronzeTemplateSignature(parsedRows[0]);
        if (templateSignature === null) {
            throw new Error("The CSV is missing a valid bronze template signature. Export a new template for the selected source entity.");
        }

        const selectedSourceEntityId = Number(bronzeSelectionIdentity.sourceEntityId);
        const selectedSourceEntityName = selectedEndpoints?.[0]?.trim() ?? "";
        if (!Number.isSafeInteger(selectedSourceEntityId) || selectedSourceEntityId <= 0 || !selectedSourceEntityName) {
            throw new Error("The selected bronze source entity could not be identified.");
        }
        if (
            templateSignature.sourceEntityId !== selectedSourceEntityId ||
            templateSignature.sourceEntityName !== selectedSourceEntityName
        ) {
            throw new Error(
                `This CSV template belongs to source entity "${templateSignature.sourceEntityName}" (${templateSignature.sourceEntityId}), ` +
                `but source entity "${selectedSourceEntityName}" (${selectedSourceEntityId}) is selected.`
            );
        }

        if (parsedRows.length < 3) {
            throw new Error("The CSV file must contain a header row and at least one record.");
        }

        const headers = parsedRows[1].map((cell) => cell.trim().toLowerCase());
        const headerIndex = new Map(headers.map((header, index) => [header, index]));
        const requiredHeaders = ["source_entity_column_name", "data_type"];
        const missingHeaders = requiredHeaders.filter((header) => !headerIndex.has(header));
        if (missingHeaders.length > 0) {
            throw new Error(`The CSV is missing required header${missingHeaders.length > 1 ? "s" : ""}: ${missingHeaders.join(", ")}.`);
        }

        const existingNames = new Set<string>();
        ((pendingBronzeImportRows ?? []) as any[]).forEach((row) => {
            const name = String(row?.source_entity_column_name ?? "").trim().toLowerCase();
            if (name) existingNames.add(name);
        });
        let existingPage = 1;
        let existingTotal = Number.POSITIVE_INFINITY;
        const existingPageSize = 250;

        // The screen normally loads the first 250 rows. Read the remaining API pages as well so
        // an imported name cannot collide with a preloaded column that is outside the current page.
        while ((existingPage - 1) * existingPageSize < existingTotal) {
            const existingResponse: any = await getBronzeCatalogColumns({
                sourceEntityId: Number(bronzeSelectionIdentity.sourceEntityId),
                page: existingPage,
                limit: existingPageSize,
            });
            const existingItems = Array.isArray(existingResponse?.items) ? existingResponse.items : [];
            existingItems.forEach((row: any) => {
                const name = String(row?.source_entity_column_name ?? "").trim().toLowerCase();
                if (name) existingNames.add(name);
            });

            existingTotal = typeof existingResponse?.total_count === "number"
                ? existingResponse.total_count
                : (existingPage - 1) * existingPageSize + existingItems.length;
            if (existingItems.length < existingPageSize) break;
            existingPage += 1;
        }
        const importedNames = new Set<string>();
        const errors: string[] = [];
        const getValue = (cells: string[], header: string) => cells[headerIndex.get(header) as number] ?? "";
        const detailFields = [
            "logical_name",
            "description",
            "constraint",
            "data_format",
            "example_values",
            "classification",
            "owner",
            "notes",
        ];
        const booleanFields = ["enable_genai", "pii"];
        const payload: CreateSourceEntityColumnInput[] = [];

        parsedRows.slice(2).forEach((cells, index) => {
            const rowNumber = index + 3;
            const name = getValue(cells, "source_entity_column_name").trim();
            const dataType = getValue(cells, "data_type").trim();
            const normalizedName = name.toLowerCase();

            if (!name) {
                errors.push(`Row ${rowNumber}: source_entity_column_name is required.`);
            }
            if (!dataType) {
                errors.push(`Row ${rowNumber}: data_type is required.`);
            }
            if (normalizedName && existingNames.has(normalizedName)) {
                errors.push(`Row ${rowNumber}: column "${name}" already exists on this entity.`);
            }
            if (normalizedName && importedNames.has(normalizedName)) {
                errors.push(`Row ${rowNumber}: column "${name}" is duplicated in the CSV.`);
            }
            if (normalizedName) {
                importedNames.add(normalizedName);
            }

            const details: Record<string, string | boolean> = {};
            detailFields.forEach((field) => {
                const value = getValue(cells, field).trim();
                if (value) details[field] = value;
            });
            booleanFields.forEach((field) => {
                const value = getValue(cells, field).trim().toLowerCase();
                if (value) {
                    if (!["true", "false"].includes(value)) {
                        errors.push(`Row ${rowNumber}: ${field} must be true or false.`);
                    } else {
                        details[field] = value === "true";
                    }
                }
            });

            if (name && dataType) {
                payload.push({
                    source_entity_column_name: name,
                    data_type: dataType,
                    ...(Object.keys(details).length > 0 ? { source_entity_column_details: details } : {}),
                });
            }
        });

        if (errors.length > 0) {
            throw Object.assign(new Error(errors.join("\n")), { errors });
        }

        const entitySample = Array.isArray((data as any)?.items) ? (data as any).items[0] : null;
        const previewRows = payload.map((column) => ({
            data_source_id: bronzeSelectionIdentity.dataSourceId,
            data_source_name: entitySample?.data_source_name ?? multipleSelection?.[0] ?? null,
            data_source_type: entitySample?.data_source_type ?? bronzeSelectionIdentity.dataSourceType,
            source_entity_id: bronzeSelectionIdentity.sourceEntityId,
            source_entity_name: entitySample?.source_entity_name ?? selectedEndpoints?.[0] ?? null,
            source_schema: entitySample?.source_schema ?? null,
            source_entity_layer: entitySample?.source_entity_layer ?? "bronze",
            source_entity_kind: entitySample?.source_entity_kind ?? null,
            source_entity_origin: entitySample?.source_entity_origin ?? null,
            source_entity_description: entitySample?.source_entity_description ?? null,
            source_entity_is_active: entitySample?.source_entity_is_active ?? true,
            source_entity_column_id: null,
            source_entity_column_name: column.source_entity_column_name,
            data_type: column.data_type,
            is_obsolete: false,
            source_entity_column_detail_id: null,
            ...column.source_entity_column_details,
        }));

        setPendingBronzeImportRows(previewRows);
        return payload.length;
    };

    const saveBronzeImport = async () => {
        if (pendingBronzeImportRows.length === 0) return;
        setIsImportingBronzeCsv(true);
        try {
            const columns: CreateSourceEntityColumnInput[] = pendingBronzeImportRows.map((row) => {
                const details = CATALOG_ENRICHMENT_FIELDS.reduce((acc, field) => {
                    if (row?.[field] !== null && row?.[field] !== undefined && row?.[field] !== "") {
                        acc[field] = row[field];
                    }
                    return acc;
                }, {} as Record<string, any>);
                return {
                    source_entity_column_name: row.source_entity_column_name,
                    data_type: row.data_type,
                    ...(Object.keys(details).length > 0 ? { source_entity_column_details: details } : {}),
                };
            });
            await createSourceEntityColumnsBulk({
                dataSourceId: Number(bronzeSelectionIdentity.dataSourceId),
                sourceEntityId: Number(bronzeSelectionIdentity.sourceEntityId),
                columns,
            });
            await refreshSourceEntityColumns(Number(bronzeSelectionIdentity.sourceEntityId));
            setPendingBronzeImportRows([]);
        } finally {
            setIsImportingBronzeCsv(false);
        }
    };

    const cancelBronzeImport = () => {
        setPendingBronzeImportRows([]);
    };


    const handleApplyFilter = async (_page: number) => {
        setCurrentPage(1)
        await fetchResults()
    }

    const baseColumns = useMemo(() => [
        { "header": "Column Name", "field": "source_entity_column_name" },
        { "header": "Source Entity", "field": "source_entity_name" },
        { "header": "Data Source", "field": "data_source_name" },
        { "header": "Data Type", "field": "data_type" },
        { "header": "Logical Name", "field": "logical_name" },
        { "header": "Description", "field": "description" },
        { "header": "Constraint", "field": "constraint" },
        { "header": "Data Format", "field": "data_format" },
        { "header": "Example Values", "field": "example_values" },
        { "header": "Enable Genai", "field": "enable_genai" },
        { "header": "Pii", "field": "pii" },
        { "header": "Classification", "field": "classification" },
        { "header": "Owner", "field": "owner" },
        { "header": "Notes", "field": "notes" }
    ], []);

    const columnsForFilter = useMemo(() => [
        { "header": "Column Name", "field": "source_entity_column_name" },
        { "header": "Source Entity", "field": "source_entity_name" },
        { "header": "Data Source", "field": "data_source_name" },
        { "header": "Data Type", "field": "data_type" },
        { "header": "Logical Name", "field": "logical_name" },
        { "header": "Classification", "field": "classification" },
        { "header": "Owner", "field": "owner" },
    ], []);

    const isRelatedEntityMode = Boolean(activeRelatedEntity);
    const hasDataMartSelection = Array.isArray(selectedDataMartEntities) && selectedDataMartEntities.length > 0;
    const isDataMartMode = !isRelatedEntityMode && hasDataMartSelection;
    const isDataSourcesCategoryActive = !isRelatedEntityMode && !hasDataMartSelection;
    const hasBronzeObjectSelection =
        isDataSourcesCategoryActive &&
        Array.isArray(multipleSelection) &&
        multipleSelection.length > 0 &&
        Array.isArray(selectedEndpoints) &&
        selectedEndpoints.length > 0;

    useEffect(() => {
        if (!isRelatedEntityMode) {
            return;
        }
        setSilverSelectedFields([...SILVER_DEFAULT_FIELDS]);
    }, [isRelatedEntityMode, activeRelatedEntity?.target_entity_rdv_id]);

    useEffect(() => {
        const nextEntity = selectedDataMartEntities?.[0] ?? null;
        if (previousDataMartEntityRef.current !== nextEntity) {
            previousDataMartEntityRef.current = nextEntity;
            setDataMartEntityPage(1);
        }
    }, [selectedDataMartEntities]);

    const columns = isDataSourcesCategoryActive
        ? baseColumns.filter(column => !DATA_SOURCE_CATEGORY_HIDDEN_FIELDS.has(column.field))
        : baseColumns;

    const visibleSelectedItems = useMemo(() => {
        if (!isDataSourcesCategoryActive) {
            return selectedItems;
        }
        return selectedItems.filter(field => !DATA_SOURCE_CATEGORY_HIDDEN_FIELDS.has(field));
    }, [selectedItems, isDataSourcesCategoryActive]);
    const bronzeVisibleColumns = useMemo(
        () => baseColumns.filter(column => visibleSelectedItems.includes(column.field)),
        [baseColumns, visibleSelectedItems]
    );

    const debouncedValue = useDebounce(visibleSelectedItems, 3000);

    useEffect(() => {
        if (isDataSourcesCategoryActive && !hasBronzeObjectSelection) {
            setData({
                items: [],
                total_count: 0,
                page: 1,
                page_size: bronzeFetchLimit,
                data: [],
                total: 0,
                total_pages: 0
            });
            setColumnsToShow(null);
            setLoading(false);
            return;
        }
        if (!isDataSourcesCategoryActive) {
            return;
        }
        if (visibleSelectedItems.length !== 0 || formattedString !== "") {
            fetchResults();
        }
    }, [debouncedValue, isDataSourcesCategoryActive, hasBronzeObjectSelection]);

    useEffect(() => {
        if (hasMounted.current) {
            if (selectedItemsForFilters.length === 0) {
                fetchResults();
            }
        } else {
            hasMounted.current = true;
        }
    }, [selectedItemsForFilters]);

    useEffect(() => {
        const payload = Array.isArray((data as any)?.items)
            ? (data as any).items
            : Array.isArray((data as any)?.data)
                ? (data as any).data
                : [];
        if (payload.length !== 0) {
            if (isDataSourcesCategoryActive) {
                setColumnsToShow((current: any) => {
                    const currentFields = Array.isArray(current) ? current.map((column: any) => column.field).join("|") : "";
                    const nextFields = bronzeVisibleColumns.map((column: any) => column.field).join("|");
                    return currentFields === nextFields ? current : bronzeVisibleColumns;
                });
            } else {
                const excludeFields = DATA_SOURCE_CATEGORY_HIDDEN_FIELDS;
                setColumnsToShow(
                    convertObjectToColumns(payload[0], {
                        excludeFields,
                    })
                );
            }
        }
    }, [data, isDataSourcesCategoryActive, bronzeVisibleColumns]);

    useEffect(() => {
        let isMounted = true;

        if (!isDataMartMode) {
            setDataMartEntityColumns([]);
            setDataMartEntityData([]);
            setDataMartEntityLoading(false);
            setGoldSelectedFields([...GOLD_DEFAULT_FIELDS]);
            setDataMartEntityPage(1);
            setDataMartEntityPageSize(DATA_MART_COLUMNS_DEFAULT_PAGE_SIZE);
            setDataMartEntityTotal(0);
            return () => {
                isMounted = false;
            };
        }

        const targetEntity = selectedDataMartEntities[0];
        if (!targetEntity) {
            setDataMartEntityColumns([]);
            setDataMartEntityData([]);
            setDataMartEntityLoading(false);
            setDataMartEntityTotal(0);
            return () => {
                isMounted = false;
            };
        }

        const selectedMapping = Array.isArray(dataMartMappings)
            ? dataMartMappings.find((item: any) => item?.target_entity_dm_name === targetEntity)
            : undefined;
        const targetEntityDmId = Number(selectedMapping?.target_entity_dm_id);
        if (!Number.isFinite(targetEntityDmId)) {
            setDataMartEntityColumns([]);
            setDataMartEntityData([]);
            setDataMartEntityLoading(false);
            setDataMartEntityTotal(0);
            return () => {
                isMounted = false;
            };
        }

        setDataMartEntityLoading(true);
        getDataMartColumns({
            targetEntityDmId,
            page: 1,
            limit: explorerFetchLimit
        })
            .then(response => {
                if (!isMounted) {
                    return;
                }

                const payload = Array.isArray((response as any)?.items)
                    ? (response as any).items
                    : Array.isArray((response as any)?.data?.items)
                        ? (response as any).data.items
                        : Array.isArray(response)
                            ? response
                            : Array.isArray((response as any)?.data)
                                ? (response as any).data
                                : [];

                const rowsToUse = payload;
                const responseMeta = (response as any) || {};
                const resolvedPage =
                    responseMeta.page ??
                    responseMeta?.data?.page ??
                    1;
                const resolvedTotalCount =
                    responseMeta.total_count ??
                    responseMeta?.data?.total_count ??
                    rowsToUse.length;

                const augmentedRows = rowsToUse;
                setDataMartEntityData(augmentedRows);
                if (augmentedRows.length > 0) {
                    setDataMartEntityColumns(
                        GOLD_FIELD_CONFIG.map(option => ({ header: option.header, field: option.field }))
                    );
                    setGoldSelectedFields(prev => {
                        const merged = [...GOLD_DEFAULT_FIELDS, ...prev];
                        return Array.from(new Set(merged));
                    });
                } else {
                    setDataMartEntityColumns(
                        GOLD_FIELD_CONFIG.map(option => ({ header: option.header, field: option.field }))
                    );
                    setGoldSelectedFields([...GOLD_DEFAULT_FIELDS]);
                }
                setDataMartEntityPage(resolvedPage);
                setDataMartEntityPageSize(itemsPerPage);
                setDataMartEntityTotal(augmentedRows.length || resolvedTotalCount);
            })
            .catch(error => {
                if (!isMounted) {
                    return;
                }
                console.error("Error fetching data mart columns:", error);
                setDataMartEntityData([]);
                setDataMartEntityColumns([]);
                setDataMartEntityTotal(0);
            })
            .finally(() => {
                if (isMounted) {
                    setDataMartEntityLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [selectedDataMartEntities, isDataMartMode, dataMartMappings]);

    const handleSelect = (item: any) => {
        setLoading(true);

        if (item.field === 'source_entity_column_name') {
            return;
        }

        if (selectedItems.includes(item.field)) {

            setSelectedItems(selectedItems.filter(i => i !== item.field));
        } else {

            setSelectedItems([...selectedItems, item.field]);
        }
    };

    const toggleSilverFieldSelection = (field: string) => {
        if (!SILVER_FIELD_KEYS.includes(field) || SILVER_LOCKED_FIELD_SET.has(field)) {
            return;
        }
        setSilverSelectedFields(prev =>
            prev.includes(field)
                ? prev.filter(item => item !== field)
                : [...prev, field]
        );
    };

    const toggleGoldFieldSelection = (field: string) => {
        const availableFields = dataMartEntityColumns?.map((column: any) => column.field) ?? [];
        if (
            !availableFields.includes(field) ||
            GOLD_LOCKED_FIELD_SET.has(field)
        ) {
            return;
        }
        setGoldSelectedFields(prev =>
            prev.includes(field)
                ? prev.filter(item => item !== field)
                : [...prev, field]
        );
    };
    const selectAllSilverFields = () => {
        setSilverSelectedFields([
            ...SILVER_DEFAULT_FIELDS,
            ...SILVER_FIELD_CONFIG.filter(item => !item.locked).map(item => item.field),
        ]);
    };
    const resetSilverFieldSelection = () => {
        setSilverSelectedFields([...SILVER_DEFAULT_FIELDS]);
    };
    const selectAllGoldFields = () => {
        const availableFields = new Set(dataMartEntityColumns?.map((column: any) => column.field) ?? []);
        const nextFields = GOLD_FIELD_CONFIG
            .filter(item => availableFields.has(item.field) || GOLD_DEFAULT_FIELDS.includes(item.field))
            .map(item => item.field);
        setGoldSelectedFields(Array.from(new Set(nextFields)));
    };
    const resetGoldFieldSelection = () => {
        setGoldSelectedFields([...GOLD_DEFAULT_FIELDS]);
    };

    const convertStringToFormField = (fieldName: string): { controlId: string, label: string, type: string, name: string } => {
        const label = fieldName.replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

        const controlId = `form${label.replace(/\s/g, '')}`;
        const name = fieldName.toLowerCase();

        return { controlId, label, type: 'text', name };
    };

    const handleSelectForFilter = (item: any) => {
        if (selectedItemsForFilters.includes(item)) {
            setSelectedItemsForFilters(selectedItemsForFilters.filter(i => i !== item));
            setFormFields(formFields.filter(field => field.name !== item));

            const keyValuePairs = formattedString.split(',');
            const filteredPairs = keyValuePairs.filter(pair => pair.split(":")[0] !== item);

            setFormattedString(filteredPairs.length === 0 ? "" : filteredPairs.join(','));
            setFormData({
                ...formData,
                [item]: ""
            });
        } else {
            setSelectedItemsForFilters([...selectedItemsForFilters, item]);
            const exists = formFields.some(field => field.name === item);
            if (!exists) {
                setFormFields(prev => [...prev, convertStringToFormField(item)]);
            }
        }
    };

    const handleChange = (e: any) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    useEffect(() => {
        const formattedStringInThisContext = selectedItemsForFilters
            .filter((key) => formData[key] !== '' && formData[key] !== null && formData[key] !== undefined)
            .map((key) => `${key}:${formData[key]}`)
            .join(',');
        setFormattedString(formattedStringInThisContext);
    }, [formData]);

    const handleEditClick = (row: any) => {
        setCatalogModalMode('edit');
        setModalFormData(row);
        setModalOriginalData(row);
        setShowModal(true);
    };

    const handleCreateClick = () => {
        setCatalogModalMode('create');
        setModalOriginalData(null);
        setModalFormData(createEmptyModalFormData());
        setShowModal(true);
    };

    const handleInputChange = (e: any) => {
        const { name, value } = e.target;
        if (name === "enable_genai" || name === "pii") {
            const { checked } = e.target;
            setModalFormData({
                ...modalFormData,
                [name]: checked
            });
        } else {
            setModalFormData({
                ...modalFormData,
                [name]: value
            });
        }
    };

    const handleSave = async () => {
        const dataSourceId = Number(modalOriginalData?.data_source_id ?? bronzeSelectionIdentity.dataSourceId);
        const sourceEntityId = Number(modalOriginalData?.source_entity_id ?? bronzeSelectionIdentity.sourceEntityId);
        const sourceEntityColumnId = Number(modalOriginalData?.source_entity_column_id);

        try {
            setSavingCatalogColumn(true);

            if (catalogModalMode === 'create') {
                const sourceEntityColumnName = String(modalFormData?.source_entity_column_name ?? '').trim();
                const dataType = String(modalFormData?.data_type ?? '').trim();

                if (!Number.isFinite(dataSourceId) || !Number.isFinite(sourceEntityId)) {
                    console.error("Missing source entity identity for catalog column create.");
                    return;
                }

                if (!sourceEntityColumnName || !dataType) {
                    console.error("Column name and data type are required for catalog column create.");
                    return;
                }

                const source_entity_column_details = CATALOG_ENRICHMENT_FIELDS.reduce((acc, field) => {
                    const currentValue = modalFormData?.[field];
                    const normalizedValue =
                        currentValue === '' || currentValue === undefined
                            ? null
                            : currentValue;
                    if (normalizedValue !== null && normalizedValue !== false) {
                        acc[field] = normalizedValue;
                    }
                    if ((field === 'enable_genai' || field === 'pii') && currentValue === true) {
                        acc[field] = true;
                    }
                    return acc;
                }, {} as Record<string, any>);

                await createCatalogueSourceColumn({
                    dataSourceId,
                    sourceEntityId,
                    payload: {
                        source_entity_column_name: sourceEntityColumnName,
                        data_type: dataType,
                        ...(Object.keys(source_entity_column_details).length > 0
                            ? { source_entity_column_details }
                            : {}),
                    },
                });
                await refreshSourceEntityColumns(sourceEntityId);
                setShowModal(false);
                setModalOriginalData(null);
                return;
            }

            if (!Number.isFinite(dataSourceId) || !Number.isFinite(sourceEntityId) || !Number.isFinite(sourceEntityColumnId)) {
                console.error("Missing source column identity for catalog enrichment save.");
                return;
            }

            const patch = CATALOG_ENRICHMENT_FIELDS.reduce((acc, field) => {
                const currentValue = modalFormData?.[field];
                const originalValue = modalOriginalData?.[field];
                if (currentValue !== originalValue) {
                    acc[field] = currentValue;
                }
                return acc;
            }, {} as Record<string, any>);

            if (Object.keys(patch).length === 0) {
                setShowModal(false);
                return;
            }

            await updateCatalogueData({
                dataSourceId,
                sourceEntityId,
                sourceEntityColumnId,
                patch,
            });
            await refreshSourceEntityColumns(sourceEntityId);
            setShowModal(false);
        } catch (error) {
            console.error("Error saving catalog column:", error);
        } finally {
            setSavingCatalogColumn(false);
        }
    };

    const canDeleteCurrentCatalogColumn =
        modalOriginalData?.data_source_type === "INTERNAL_REGISTERED" &&
        Number.isFinite(Number(modalOriginalData?.source_entity_column_id));

    const handleDeleteCatalogColumn = async () => {
        const dataSourceId = Number(modalOriginalData?.data_source_id ?? bronzeSelectionIdentity.dataSourceId);
        const sourceEntityId = Number(modalOriginalData?.source_entity_id ?? bronzeSelectionIdentity.sourceEntityId);
        const sourceEntityColumnId = Number(modalOriginalData?.source_entity_column_id);

        if (!Number.isFinite(dataSourceId) || !Number.isFinite(sourceEntityId) || !Number.isFinite(sourceEntityColumnId)) {
            console.error("Missing source column identity for catalog column delete.");
            return;
        }

        const confirmed = window.confirm(
            `Delete column "${modalOriginalData?.source_entity_column_name ?? "column"}" from this internal asset?`
        );
        if (!confirmed) {
            return;
        }

        try {
            setDeletingCatalogColumn(true);
            await deleteCatalogueSourceColumn({
                dataSourceId,
                sourceEntityId,
                sourceEntityColumnId,
            });
            await refreshSourceEntityColumns(sourceEntityId);
            setShowModal(false);
            setModalOriginalData(null);
        } catch (error) {
            console.error("Error deleting catalog column:", error);
        } finally {
            setDeletingCatalogColumn(false);
        }
    };

    return {
        data,
        columnsToShow,
        showModal,
        // Used by the standalone AddColumnsModal to refresh the column list after a bulk create.
        refreshSourceEntityColumns,
        formattedString,
        selectedItems,
        visibleSelectedItems,
        selectedItemsForFilters,
        modalFormData,
        modalOriginalData,
        loading,
        formFields,
        columns,
        bronzeEditColumns: baseColumns,
        columnsForFilter,
        formData,
        multipleSelection,
        selectedEndpoints,
        bronzeSelectionIdentity,
        selectedDataMarts,
        selectedDataMartEntities,
        dataMartMappings,
        dataMartOptions,
        isLoadingDataMartMappings,
        relatedEntityColumns,
        relatedEntityData,
        relatedEntityLoading,
        relatedEntityPage,
        relatedEntityTotal,
        relatedEntityPageSize,
        relatedEntityHasMore,
        relatedEntityTotalKnown,
        dataMartEntityColumns,
        dataMartEntityData,
        dataMartEntityLoading,
        dataMartEntityPage,
        dataMartEntityPageSize,
        dataMartEntityTotal,
        activeRelatedEntity,
        setActiveRelatedEntity,
        isRelatedEntityMode,
        isDataMartMode,
        showErrorModal,
        itemsPerPage,
        currentPage,
        apiFailedStatus,
        setShowModal,
        setFormattedString,
        setSelectedItems,
        setSelectedItemsForFilters,
        setModalFormData,
        setModalOriginalData,
        setLoading,
        setFormFields,
        handleSelect,
        handleSelectForFilter,
        handleChange,
        handleEditClick,
        handleCreateClick,
        handleInputChange,
        handleSave,
        handleDeleteCatalogColumn,
        canDeleteCurrentCatalogColumn,
        deletingCatalogColumn,
        catalogModalMode,
        savingCatalogColumn,
        isInternalSourceEntitySelected,
        handleApplyFilter,
        setData,
        setMultipleSelection,
        setSelectedEndpoints,
        setBronzeSelectionIdentity,
        setSelectedDataMarts,
        setSelectedDataMartEntities,
        setShowErrorModal,
        handlePageChange,
        handleRelatedEntityPageChange,
        handleDataMartEntityPageChange,
        downloadCSV,
        downloadBronzeTemplate,
        downloadBronzeGuideFile,
        importBronzeCsv,
        isImportingBronzeCsv,
        pendingBronzeImportRows,
        saveBronzeImport,
        cancelBronzeImport,
        isLoadingDownloadCSV,
        silverFieldOptions: SILVER_FIELD_CONFIG,
        silverSelectedFields,
        toggleSilverFieldSelection,
        selectAllSilverFields,
        resetSilverFieldSelection,
        silverLockedFields: SILVER_LOCKED_FIELD_SET,
        goldFieldOptions: GOLD_FIELD_CONFIG,
        goldSelectedFields,
        toggleGoldFieldSelection,
        selectAllGoldFields,
        resetGoldFieldSelection,
        goldLockedFields: GOLD_LOCKED_FIELD_SET,
        bronzeDefaultFields: BRONZE_DEFAULT_FIELDS
    };
};

const convertObjectToColumns = (
    obj: any,
    options?: {
        excludeFields?: Iterable<string>;
        shouldExcludeKey?: (key: string) => boolean;
    }
) => {
    const keysToModify = [
        "source_entity_column_name",
        "source_entity_name",
        "data_source_name"
    ];
    const keysToExclude = new Set([
        "source_entity_column_id",
        "source_entity_column_detail_id",
        "source_entity_column_details_id",
        "name"
    ]);
    const additionalExclusions = new Set(
        Array.from(options?.excludeFields ?? []).map(field => field.toLowerCase())
    );
    const shouldExcludeKey = options?.shouldExcludeKey;

    const modified_object = Object.keys(obj)
        .filter(key => {
            const normalizedKey = key.toLowerCase();
            if (keysToExclude.has(normalizedKey)) {
                return false;
            }
            if (additionalExclusions.size > 0 && additionalExclusions.has(normalizedKey)) {
                return false;
            }
            if (shouldExcludeKey && shouldExcludeKey(key)) {
                return false;
            }
            return true;
        })
        .map(key => {
            let modifiedKey = key;
            if (keysToModify.includes(key)) {
                modifiedKey = key.replace('_name', '');
            }

            let header = modifiedKey.replace(/_/g, ' ')
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');

            header = header.replace(/Target Entity Dm\s*/gi, "").replace(/\s{2,}/g, " ").trim();

            if (key === "source_entity_column_name") {
                header = "Column Name";
            }

            return { header: header, field: key };
        });

    return modified_object;
};
